import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Controller } from 'react-hook-form'
import { DownloadButtons } from './DownloadButtons'
import { FormValues } from '@app/code/src/types/auth-form.types'
import { GENERATOR_GOOGLE_SSO_REDIS } from '@dtx-company/true-common/src/constants/featureFlags'
import { GoogleButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { RecaptchaPPAndToS } from '@dtx-company/inter-app/src/components/singleSignOn/RecaptchaPPAndToS/RacaptchaPPAndToS'
import { SSORedirect } from '@dtx-company/inter-app/src/components/singleSignOn/SSORedirect'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { theme } from '@dtx-company/shared-components/src'
import { useBuildCreateCodePayload } from '../CreateFlowcodeForm/CreateFlowcodeFormDesktop/CreateFlowcodeFormDesktop.hooks'
import { useCreateFlowcodeForm } from '../CreateFlowcodeForm/CreateFlowcodeForm.hooks'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useGoogleLoginUrl } from '@app/common/src/hooks/useGoogleLoginUrl'
import { useSetGeneratorFormCache } from '@app/code/src/utils/flowcode/generatorFormCache'
import { useSimplifiedDesktopSignUp } from './SimplifiedDesktop.hooks'
import React from 'react'
import StepHeader from './StepHeader'
import homepageEvents from '@dtx-company/inter-app/src/event-tracking/events/homepage'
import isEmail from 'validator/lib/isEmail'
import proConversionEvents from '@dtx-company/inter-app/src/event-tracking/events/proConversion'
import useTranslation from 'next-translate/useTranslation'

export const LoggedOutDownloadStep: React.FC<{
  formRef?: React.LegacyRef<HTMLFormElement>
  onSuccessLogIn: () => void
}> = ({ formRef, onSuccessLogIn }) => {
  const [generatorGoogleSsoRedisEnabled] = useFlowFeature(GENERATOR_GOOGLE_SSO_REDIS)
  const {
    authenticateUser,
    checkForValidEmail,
    control,
    disabled,
    error,
    emailIsSSOEnabled,
    emailOnChange,
    handleSubmit,
    submitting,
    ssoRedirectHtml,
    startSSORedirect
  } = useSimplifiedDesktopSignUp(onSuccessLogIn)
  const { t } = useTranslation('auth')
  const createCodeArgs = useBuildCreateCodePayload()
  const { trigger: triggerCreateFlowcodeFormValidation } = useCreateFlowcodeForm()
  const { url: googleLoginUrl } = useGoogleLoginUrl('signup')
  const setGeneratorFormCache = useSetGeneratorFormCache()

  const onSubmit = async (data: FormValues): Promise<void> => {
    const createFlowcodeFormIsValid = await triggerCreateFlowcodeFormValidation()

    if (!createFlowcodeFormIsValid) return

    if (emailIsSSOEnabled) {
      if (generatorGoogleSsoRedisEnabled) {
        await setGeneratorFormCache(createCodeArgs)
        startSSORedirect()
        return
      }
    } else {
      await authenticateUser(data)
    }
  }

  const onGoogleSigninClick = async (): Promise<void> => {
    const createFlowcodeFormIsValid = await triggerCreateFlowcodeFormValidation()
    if (!createFlowcodeFormIsValid) return

    proConversionEvents.Flowcode_Homepage_Clicked_Signup_Google()

    if (generatorGoogleSsoRedisEnabled) {
      await setGeneratorFormCache(createCodeArgs)
      window.location.href = googleLoginUrl
      return
    }

    window.location.href = googleLoginUrl
  }

  const googleButtonDisabled = emailIsSSOEnabled || submitting

  return (
    <>
      <SSORedirect redirectFormHtml={ssoRedirectHtml} />
      <Box flexDirection="column" data-testid="logged-out-download-step">
        <Spacer mb="12px" />
        <Box alignItems="baseline">
          <Box alignItems="center" ml="-32px">
            <Text minWidth="92px" color="primary.grey">
              {t('Social.ContinueWith')}
            </Text>
            <Spacer mr="7px" />
            {googleLoginUrl && (
              <>
                <GoogleButton
                  disabled={googleButtonDisabled}
                  type="button"
                  width="111px"
                  onClick={onGoogleSigninClick}
                  iconAlt=""
                  height="40px"
                  border="1px solid #D64B2F"
                  justifyContent="center"
                >
                  <Text
                    variant="body/small"
                    as="span"
                    fontWeight={400}
                    color="primary.black"
                    aria-label="Sign up with Google"
                  >
                    {t('Social.Google')}
                  </Text>
                </GoogleButton>
              </>
            )}
            <Text color="primary.grey" mx="9px">
              {t('Social.Or')}
            </Text>
          </Box>

          <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
            <Box width="100%" justifyContent="space-between">
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, ref, value, ...rest }, fieldState: { error } }) => (
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    helperText={error?.type === 'required' ? 'Email is required' : error?.message}
                    error={Boolean(error)}
                    inputProps={{
                      'data-heap-id': 'LoggedOutDownloadStep_EmailInput',
                      'aria-label': 'eamil address',
                      ...rest,
                      onFocus: homepageEvents.userFocusedEmailInput
                    }}
                    id="auth-form-email-input"
                    onChange={e => {
                      const emailAddress = e.target?.value
                      const isEmailValid = isEmail(emailAddress)
                      emailOnChange(e.target.value)
                      checkForValidEmail(isEmailValid)
                      onChange(e)
                    }}
                    ref={ref}
                    value={value}
                  />
                )}
              />
              <Spacer mr="16px" />
              {emailIsSSOEnabled ? (
                <Box
                  minWidth="200px"
                  mt="-4px"
                  justifyContent="center"
                  alignItems="center"
                  data-testid="sso-label"
                >
                  <Icon src="/icons/lock-grey.svg" />{' '}
                  <Box flexDirection="column">
                    <StepHeader title={t('SSO.StepHeader.Title')} />
                  </Box>
                </Box>
              ) : (
                <Controller
                  control={control}
                  name="password"
                  render={({ field: { ref, value, ...rest }, fieldState: { error } }) => (
                    <Input
                      type="password"
                      placeholder={t('PasswordField.Placeholder')}
                      helperText={
                        error?.type === 'required'
                          ? t('PasswordField.Errors.Required')
                          : error?.message
                      }
                      error={Boolean(error)}
                      inputProps={{
                        ...rest,
                        'aria-label': 'password',
                        'data-heap-id': 'LoggedOutDownloadStep_PasswordInput',
                        onFocus: homepageEvents.userFocusedPasswordInput
                      }}
                      ref={ref}
                      value={value}
                    />
                  )}
                />
              )}
              <Spacer mr="16px" />
              <Box width="100%" height="44px">
                <DownloadButtons
                  loading={submitting}
                  disabled={disabled}
                  emailIsSSOEnabled={emailIsSSOEnabled}
                />
              </Box>
            </Box>
          </form>
        </Box>

        <RecaptchaPPAndToS textAlign="right" />

        {error && (
          <Text
            margin="16px 0"
            textAlign="right"
            variant="body/small"
            color={theme.colors.status.errorDark}
            role="alert"
          >
            {error}
          </Text>
        )}
      </Box>
    </>
  )
}
